/**
 * i18next with English source strings as keys.
 *
 * `t('Sync Center')` renders the English string untouched and looks it up in
 * the Swahili/Arabic bundles — the same shape as the prototype's catalogs, so
 * they port across directly. Missing translations fall back to English rather
 * than showing a key. English ships in the app shell; Swahili and Arabic are
 * lazy chunks loaded on demand and cached by the service worker once used, so
 * the language a person has chosen keeps working offline (switching to a
 * language never loaded on this device needs a connection once; until then
 * the app stays in English).
 *
 * Marking strings: literal `t('…')` calls are picked up by
 * `pnpm i18n:extract`; strings defined in config (nav labels, statuses) are
 * wrapped in `msg('…')` so the extractor sees them too.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { moneyLocale } from '@/lib/money';
import { cacheLoadedAssets } from '@/lib/pwa/asset-cache';
import { getPreferences, updatePreferences, type LanguageCode } from '@/lib/preferences';

export type { LanguageCode };

export const LANGUAGES: ReadonlyArray<{
  code: LanguageCode;
  label: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}> = [
  { code: 'en', label: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪', dir: 'ltr' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', dir: 'rtl' },
];

type Bundle = Record<string, string>;

const loaders: Record<Exclude<LanguageCode, 'en'>, () => Promise<{ default: Bundle }>> = {
  sw: () => import('./locales/sw.json'),
  ar: () => import('./locales/ar.json'),
};

/** Identity marker for strings translated later via `t(value)`. */
export function msg<const T extends string>(value: T): T {
  return value;
}

export function directionOf(language: string): 'ltr' | 'rtl' {
  return LANGUAGES.find((entry) => entry.code === language)?.dir ?? 'ltr';
}

/** Set `lang`/`dir` on <html> so logical CSS properties and Radix portals flip. */
export function applyDocumentLanguage(language: LanguageCode): void {
  const root = document.documentElement;
  root.lang = language;
  root.dir = directionOf(language);
}

async function ensureBundle(language: LanguageCode): Promise<void> {
  if (language === 'en' || i18next.hasResourceBundle(language, 'translation')) return;
  const bundle = await loaders[language]();
  i18next.addResourceBundle(language, 'translation', bundle.default, true, true);
  cacheLoadedAssets();
}

let initialised: Promise<void> | null = null;

export function initI18n(language: LanguageCode = getPreferences().language): Promise<void> {
  initialised ??= i18next
    .use(initReactI18next)
    .init({
      lng: 'en',
      fallbackLng: 'en',
      resources: { en: { translation: {} } },
      keySeparator: false,
      nsSeparator: false,
      returnNull: false,
      returnEmptyString: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    })
    .then(() => undefined);
  return initialised.then(() => setLanguage(language, { persist: false }));
}

export async function setLanguage(
  language: LanguageCode,
  { persist = true }: { persist?: boolean } = {},
): Promise<void> {
  try {
    await ensureBundle(language);
  } catch {
    // Bundle unavailable (offline before it was ever cached): stay readable in
    // English rather than half-switching.
    language = 'en';
  }
  await i18next.changeLanguage(language);
  applyDocumentLanguage(language);
  if (persist) updatePreferences({ language });
}

/** Locale for Intl formatting in the active language. */
export function activeLocale(): string {
  return moneyLocale(i18next.language || 'en');
}

export { i18next };
